// useRecipientProfile.js
// Fetches and caches the live GlobalUserProfile for a recipient
// after they've been added from search. This guarantees we always
// show the same photo GlobalUserProfile.jsx would render.
//
// Resolution order (mirrors GlobalUserProfile.jsx exactly):
//   1. If creator_username exists → API.creators.fetchDetails(creator_username)
//      → returns { creator: { pfphref, display_name, ... } }
//   2. Else → API.users.fetchPublicDetails(global_user_id)
//      → returns { pfphref, first_name, last_name, ... }
//
// The hook returns a Map: globalUserId → { pfphref, displayName, isCreator }
// Components read from it and fall back to the search-result pfphref.

import { useState, useCallback } from "react";
import client from "../../../API";

export function useRecipientProfile() {
  // Map<string, { pfphref, displayName, isCreator, loading, error }>
  const [profiles, setProfiles] = useState({});

  const fetchProfile = useCallback(async (recipient) => {
    if (recipient.kind !== "internal" || !recipient.globalUserId) return;

    const key = String(recipient.globalUserId);

    // Already fetched or in flight
    setProfiles((prev) => {
      if (prev[key]) return prev;
      return { ...prev, [key]: { pfphref: recipient.pfphref, displayName: recipient.label, isCreator: !!recipient.creatorUsername, loading: true, error: false } };
    });

    try {
      let pfphref    = null;
      let displayName = recipient.label;
      let isCreator   = false;

      const creatorSlug = recipient.creatorUsername || recipient.username;

      if (creatorSlug) {
        // Creator path — same as GlobalUserProfile
        const data = await client.creators.fetchDetails(creatorSlug);
        const c    = data?.creator || data;
        pfphref     = c?.pfphref || c?.profile_photo || null;
        displayName = c?.display_name || c?.creator || displayName;
        isCreator   = true;
      } else {
        // Normal user path
        const data  = await client.users.fetchPublicDetails(String(recipient.globalUserId));
        pfphref     = data?.pfphref || data?.profile_photo || null;
        const fn    = data?.first_name || "";
        const ln    = data?.last_name  || "";
        displayName = (fn + " " + ln).trim() || data?.username || displayName;
        isCreator   = false;
      }

      setProfiles((prev) => ({
        ...prev,
        [key]: { pfphref, displayName, isCreator, loading: false, error: false },
      }));
    } catch (err) {
      // On error, keep whatever the search gave us
      setProfiles((prev) => ({
        ...prev,
        [key]: { ...(prev[key] || {}), loading: false, error: true },
      }));
    }
  }, []);

  // Resolve the best available profile data for a recipient
  const resolve = useCallback((recipient) => {
    const key = String(recipient.globalUserId || "");
    const cached = profiles[key];
    return {
      pfphref:     cached?.pfphref     ?? recipient.pfphref     ?? null,
      displayName: cached?.displayName ?? recipient.label       ?? "",
      isCreator:   cached?.isCreator   ?? !!recipient.creatorUsername,
      loading:     cached?.loading     ?? false,
    };
  }, [profiles]);

  return { fetchProfile, resolve };
}